import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { selectInputs } from '../redux/inputsReducer';
import { selectDealerAndVehicleData } from '../redux/dealerAndVehicleReducer';
import { selectAppConfig } from '../redux/appConfigReducer';
import { CIGS_URL, ELSP_URL } from '../constants/environment';
import { SubmissionData, CASubmissionData } from '../types';
import { prepareDealerIdForFormsApi } from '../functions/dealerIdConverter';
import { US_MARKET, FEATURE_APP_DETAILS } from '../constants';
import { reversedPhoneNumberFormatter } from '../functions/phoneNumberFormatter';
import { convertToPromotionsType } from '../functions/convertToPromotionsType';
import useSourceOfLead from './useSourceOfLead';
import { digitalLeadsFormSubmissionError } from '../utils/analyticsEventListeners';
import { selectForm } from '../redux/formReducer';
import formTypeFormatter from '../functions/formTypeFormatter';

const getCapitalized = (word: string | undefined): string => {
  if (!word) return '';
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
};

export const useElspFormSubmission = () => {
  const {
    firstName,
    lastName,
    phoneNumber,
    email,
    zipCode,
    preferredContactMethod,
    preferredContactTime,
    ccpa,
    tcpaConsent,
    universalConsent,
    dealerConsent,
    oemConsent,
    topicOfInterest,
  } = useSelector(selectInputs);

  const { vehicle } = useSelector(selectDealerAndVehicleData);
  const { dealerId, market, adobeCloudId, device, ctaInnerText, campaignId, offers } =
    useSelector(selectAppConfig);
  const contactForm = useSelector(selectForm);
  const { url, generalSource, specificSource } = useSourceOfLead();

  const promotion = convertToPromotionsType(offers!);
  const finalDealerId = dealerId || vehicle?.dealerId;
  const isVehicleInfoAvailable = !!vehicle;

  const isUSMarket = market === US_MARKET || market?.toLowerCase().includes('us');
  const isTier3 = generalSource === 'tier_3';

  // ---------------------- Dealer Info to submit -----------------------//
  const dealerToSubmit = finalDealerId && { id: prepareDealerIdForFormsApi(finalDealerId) };

  // ---------------------- Vehicle Info to submit -----------------------//
  const vehicleToSubmit = useMemo(() => {
    if (!vehicle) return undefined;
    const {
      vin,
      stockNumber,
      modelPrice,
      vehicleType,
      vehicleInventoryType,
      modelYear,
      modelMake,
      modelName,
      trimName,
      exteriorColor,
      bodyType,
      fuelType,
      oaddStockNumber,
    } = vehicle;
    return {
      vin,
      stockNumber: isTier3 ? oaddStockNumber : stockNumber,
      msrpTotal: modelPrice,
      status: vehicleInventoryType === 'CPO' ? 'CPO' : vehicleType,
      year: modelYear,
      make: modelMake,
      model: modelName,
      trim: trimName,
      exteriorColor,
      bodyType,
      fuelType,
    };
  }, [isTier3, vehicle]);

  // ---------------------- Offer Details to submit -----------------------//
  const offerDataToSubmit = useMemo(() => {
    if (!offers?.offerDetails) return undefined;
    const { creditBonus, customerDownPayment, monthlyPayment, term, type, cpoTerm, aprRate } =
      offers.offerDetails;
    const termForCPO = Number((cpoTerm && cpoTerm.match(/\d+$/)?.[0]) || 0);
    const normilizedTerm = type === 'NEW-CAR' ? term : termForCPO;
    return {
      offerType: offers.offerType,
      creditBonus: creditBonus ?? 0,
      downPayment: customerDownPayment ?? 0,
      monthlyPayment: Number(monthlyPayment?.replace(/[$,]/g, '')) ?? 0,
      term: normilizedTerm ?? 0,
      apr: aprRate,
    };
  }, [offers?.offerDetails, offers?.offerType]);

  const offerVehicleDataToSubmit = useMemo(() => {
    if (!offers) return undefined;
    const { type } = offers.offerDetails;
    const status = type === 'NEW-CAR' ? 'new' : 'CPO';
    return {
      model: offers?.modelName,
      year: offers?.modelYear,
      trim: offers?.trimName,
      status,
      make: 'Audi',
    };
  }, [offers]);

  // ---------------------- Full submition data -----------------------//
  const submissionData: SubmissionData = {
    metaData: {
      source: generalSource,
      type: formTypeFormatter(isVehicleInfoAvailable, specificSource, topicOfInterest),
      device,
      adobeCloudId,
      market: isUSMarket ? 'US' : 'CA',
      ctaVerbiage: ctaInnerText || '',
      dealerUrl: url,
      campaignId,
    },
    inputData: {
      firstName,
      lastName,
      preferredContactMethod,
      preferredContactTime: preferredContactTime
        ? preferredContactTime.toUpperCase()
        : 'NO_PREFERENCE',
      email,
      zipCode,
      phone: phoneNumber ? reversedPhoneNumberFormatter(phoneNumber) : '',
      ...(isUSMarket
        ? {
            universalConsent: true,
            ccpa,
            tcpa: tcpaConsent,
          }
        : {
            universalConsent: generalSource === 'tier_1' ? true : universalConsent,
            dealerConsent,
            oemConsent,
          }),
    },
    ...(offerDataToSubmit ? { offer: offerDataToSubmit } : {}),
    ...(vehicleToSubmit ? { vehicle: vehicleToSubmit } : {}),
    ...(offerVehicleDataToSubmit ? { vehicle: offerVehicleDataToSubmit } : {}),
    ...(dealerToSubmit ? { dealer: dealerToSubmit } : {}),
  };

  interface AxiosLikeError extends Error {
    response?: {
      status: number;
      data: any;
    };
  }

  const ELSPGateway = isUSMarket ? `${ELSP_URL}/submit` : `${CIGS_URL}/submit`;

  const handleSubmit = async () => {
    try {
      const response = await fetch(ELSPGateway, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData),
      });

      if (!response.ok) {
        const errorData = await response.text().catch(() => '');
        const error: AxiosLikeError = new Error(`HTTP error! status: ${response.status}`);
        error.response = {
          status: response.status,
          data: errorData,
        };
        throw error;
      }

      const data = await response.json();
      return isUSMarket ? data?.response : data;
      // eslint-disable-next-line
    } catch (error: any) {
      // eslint-disable-next-line
      console.log(error);
      const errorPayload = {
        error_message: error.response?.data,
        error_code: error.response?.status,
      };
      digitalLeadsFormSubmissionError(
        contactForm,
        vehicle!,
        FEATURE_APP_DETAILS,
        promotion,
        errorPayload,
      );
      return error;
    }
  };

  return handleSubmit;
};

export const useCAFormSubmission = () => {
  const { i18n } = useTranslation();
  const lang = i18n.languages?.[0] || 'en';
  const {
    firstName,
    lastName,
    phoneNumber,
    email,
    zipCode,
    preferredContactMethod,
    universalConsent,
    tradeInVehicleMake,
    tradeInVehicleMileage,
    tradeInVehicleModel,
    tradeInVehicleVin,
    tradeInVehicleYear,
    modelLineOfInterest,
  } = useSelector(selectInputs);

  const { vehicle } = useSelector(selectDealerAndVehicleData);
  const { dealerId, adobeCloudId, campaignId } = useSelector(selectAppConfig);

  const contactForm = useSelector(selectForm);
  const { url, generalSource } = useSourceOfLead();

  const finalDealerId = dealerId || vehicle?.dealerId;

  const submissionData: CASubmissionData = {
    FirstName: firstName,
    LastName: lastName,
    Email: email,
    PostalCode: zipCode,
    ModelDescription: vehicle?.modelName || modelLineOfInterest,
    ModelYear: vehicle?.modelYear || new Date().getFullYear().toString(),
    VIN: vehicle?.vin,
    KindOfPurchase: getCapitalized(vehicle?.vehicleType),
    stockNumber: vehicle?.oaddStockNumber ?? '',
    hasDealer: !!finalDealerId,
    PartnerNumber: finalDealerId,
    microkernel: false,
    contactMethod: getCapitalized(preferredContactMethod),
    caslOptIn: universalConsent,
    LanguageCode: lang,
    ID: campaignId,
    ShortDescription: 'Contact a Dealer',
    EntryChannelDetails: 'Contact a Dealer',
    sourceParam: generalSource,
    dagCity: '',
    Number: phoneNumber ? reversedPhoneNumberFormatter(phoneNumber) : '',
    URL: url,
    adobeCloudId,
    SecondLastName: vehicle?.modelMake ?? '',
    BirthName: vehicle?.modelName ?? '',
    TradeInVehicleMake: tradeInVehicleMake,
    TradeInVehicleModel: tradeInVehicleModel,
    TradeInVehicleYear: tradeInVehicleYear,
    TradeInVehicleMileage: tradeInVehicleMileage,
    TradeInVehicleVIN: tradeInVehicleVin,
  };

  const handleSubmit = async () => {
    try {
      const response = await fetch(`${CIGS_URL}/sendOpportunity`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData),
      });
      if (!response.ok) {
        const errorData = await response.text().catch(() => '');
        const error = new Error(`HTTP error! status: ${response.status}`) as any;
        error.response = {
          status: response.status,
          data: errorData,
        };
        throw error;
      }

      if (!response.ok) {
        const errorData = await response.text().catch(() => '');
        const error = new Error(`HTTP error! status: ${response.status}`) as any;
        error.response = {
          status: response.status,
          data: errorData,
        };
        throw error;
      }

      const data = await response.json();
      return data?.response;
      // eslint-disable-next-line
    } catch (error: any) {
      // eslint-disable-next-line
      console.log(error);
      // runs when 'try again' is clicked
      const errorPayload = {
        error_message: error.response?.data,
        error_code: error.response?.status,
      };
      digitalLeadsFormSubmissionError(
        contactForm,
        vehicle!,
        FEATURE_APP_DETAILS,
        undefined,
        errorPayload,
      );
      return error;
    }
  };

  return handleSubmit;
};
